/**
 * PrivacyPolicy component displays the company's data collection and privacy practices
 * @component
 * @description Provides comprehensive information about:
 * - Data collection and processing practices
 * - Product-specific privacy details
 * - User rights and data protection measures
 * - International data transfers
 * - GDPR, CCPA, and other regulatory compliance
 * @returns {JSX.Element} The rendered PrivacyPolicy component
 */
import React from 'react';
import './Policy.css';

const PrivacyPolicy = () => {
  return (
    <div className="policy-container">
      <h1>Privacy Policy</h1>
      <p className="last-updated">Last Updated: September 14, 2025</p>

      <section>
        <h2>1. Introduction</h2>
        <p>
          TearFace ("we", "our", "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information across our products: the Accessibility Widget, Chat Support Widget, and Digital Portfolio Services.
        </p>
      </section>

      <section>
        <h2>2. Information We Collect</h2>
        
        <h3>2.1 Accessibility Widget Data</h3>
        <p>We collect information related to how users interact with the Accessibility Widget, including:</p>
        <ul>
          <li><strong>Widget interaction analytics</strong> <em>(aggregated, non-personal usage data only)</em>:
            <ul>
              <li>Feature usage frequency and patterns</li>
              <li>Session duration and engagement metrics</li>
              <li>Widget responsiveness and performance (e.g., load times, crashes, slowdowns)</li>
            </ul>
          </li>
          <li><strong>Technical information</strong>:
            <ul>
              <li>Browser type and version</li>
              <li>Device capabilities and settings</li>
              <li>Screen resolution and window size</li>
              <li>Operating system accessibility settings</li>
            </ul>
          </li>
          <li><strong>Usage patterns</strong> <em>(aggregated, non-personal)</em>:
            <ul>
              <li>Most frequently used features</li>
              <li>Common setting combinations</li>
              <li>Profile switching behavior</li>
              <li>Effectiveness of accessibility features</li>
            </ul>
          </li>
          <li><strong>Website information</strong> <em>(non-personal)</em>:
            <ul>
              <li>Page URLs where the widget is implemented</li>
              <li>Page titles</li>
              <li>Implementation status and version</li>
            </ul>
          </li>
        </ul>
        <p><strong>Note:</strong> Your accessibility preferences and settings are always stored locally in your browser and are never transmitted to our servers. Analytics and usage data is collected only in aggregated, non-personal form for service improvement and does not include your specific settings or personal information.</p>
        <p><strong>Data Storage Location:</strong></p>
        <ul>
          <li>Widget and extension preferences: Stored locally in browser storage (e.g., localStorage) and never transmitted to our servers.</li>
          <li>Analytics data: Only aggregated, non-personal data is processed and stored on secure servers.</li>
        </ul>
        <p><strong>Data Retention:</strong></p>
        <ul>
          <li>Local preferences: Until manually cleared by user.</li>
          <li>Analytics data: Aggregated after 90 days.</li>
        </ul>

        <h3>2.2 Chat Support Widget Data</h3>
        <ul>
          <li>Chat conversations and interactions</li>
          <li>User queries and AI responses</li>
          <li>Widget configuration settings</li>
          <li>Technical usage metrics</li>
        </ul>

        <h3>2.3 Portfolio Services Data</h3>
        <ul>
          <li>Account information (name, email, contact details)</li>
          <li>Portfolio content and materials you provide</li>
          <li>Custom domain information</li>
          <li>Portfolio analytics and visitor statistics</li>
        </ul>

        <h3>2.4 Contact and Waitlist Data</h3>
        <ul>
          <li>Name, email, and phone number from contact forms</li>
          <li>Message content and timestamp of submissions</li>
          <li>Waitlist registration details and preferences</li>
          <li>Technical data about your submission (IP, browser, device)</li>
          <li>Any additional information you voluntarily provide</li>
        </ul>
        <p>
          BY SUBMITTING ANY CONTACT FORM OR JOINING OUR WAITLIST, YOU EXPRESSLY CONSENT TO:
        </p>
        <ul>
          <li>Our collection and storage of your submitted information</li>
          <li>Being contacted by us through any provided contact method</li>
          <li>Receiving marketing and promotional communications (opt-out available)</li>
          <li>Your data being processed and stored as described in this policy</li>
          <li>Your data being retained until you request deletion</li>
        </ul>
      </section>

      <section>
        <h2>3. How We Use Your Information</h2>
        <p>We use collected information to:</p>
        <ul>
          <li>Provide and improve our services</li>
          <li>Customize user experience</li>
          <li>Monitor and analyze usage patterns</li>
          <li>Maintain service security</li>
          <li>Communicate with you about our services</li>
          <li>Comply with legal obligations</li>
        </ul>
      </section>

      <section>
        <h2>4. Data Storage and Security</h2>
        
        <h3>4.1 Data Storage</h3>
        <p>We store data in different ways depending on the service:</p>
        <ul>
          <li>Accessibility preferences: Stored locally in your browser</li>
          <li>Chat conversations: Temporarily stored during active sessions</li>
          <li>Portfolio content: Stored securely on our servers</li>
        </ul>

        <h3>4.2 Security Measures</h3>
        <ul>
          <li>Encryption of data in transit and at rest</li>
          <li>Regular security audits and updates</li>
          <li>Access controls and authentication</li>
          <li>Secure backup systems</li>
        </ul>
      </section>

      <section>
        <h2>5. Data Sharing and Third Parties</h2>
        <p>We may share data with:</p>
        <ul>
          <li>Service providers who assist in operating our services</li>
          <li>Analytics providers (in anonymized form)</li>
          <li>Law enforcement when required by law</li>
        </ul>
        <p>We do not sell your personal information to third parties.</p>
      </section>

      <section>
        <h2>6. Your Rights and Choices</h2>
        <p>You have the right to:</p>
        <ul>
          <li>Access your personal data</li>
          <li>Correct inaccurate data</li>
          <li>Request deletion of your data</li>
          <li>Export your data</li>
          <li>Opt-out of certain data collection</li>
          <li>Withdraw consent for data processing</li>
        </ul>
      </section>

      <section>
        <h2>7. Data Storage and Communication</h2>
        <p>
          We operate with minimal data storage:
        </p>
        <ul>
          <li>Widget preferences and settings are stored locally in your browser only</li>
          <li>Contact form submissions and waitlist registrations are handled through standard email communications</li>
          <li>Portfolio content is publicly accessible as intended</li>
          <li>We do not operate data centers or store personal information on servers</li>
        </ul>
      </section>

      <section>
        <h2>8. Service-Specific Privacy Details</h2>
        <h3>8.1 Accessibility Widget</h3>
        <ul>
          <li>Processing of accessibility preferences and settings</li>
          <li>Collection of usage metrics for service improvement</li>
          <li>Navigation patterns and keyboard usage</li>
          <li>Color and contrast preferences</li>
        </ul>

        <h3>8.2 Age Restrictions</h3>
        <p>
          Our Services are not intended for and may not be used by individuals under the age of 13. We do not knowingly collect information from anyone under 13 years of age, in compliance with COPPA (Children's Online Privacy Protection Act). If we discover that we have collected personal information from a person under 13, we will delete that information immediately.
        </p>
      </section>

      <section>
        <h2>9. Updates to This Policy</h2>
        <p>
          We may update this Privacy Policy to reflect changes in our practices or for legal, operational, or regulatory reasons. We will notify you of any material changes through our website or email.
        </p>
      </section>

      <section>
        <h2>10. Contact Information</h2>
        <p>
          For questions about this Privacy Policy or our data practices, please contact:
        </p>
        <p>Email: hellotearface@gmail.com</p>
      </section>

      <section>
        <h2>11. No Waiver and Severability</h2>
        <p>
          If any part of this Privacy Policy is found to be invalid, unenforceable, or incomplete, the remaining provisions will remain in full force and effect. Our failure to enforce any right or provision of this policy does not constitute a waiver of that right or provision. No omission, error, or imperfection in this document will create any liability or waive any limitation of liability, and you agree that our liability remains limited as described herein regardless of any such issue.
        </p>
      </section>
    </div>
  );
};

export default PrivacyPolicy;
